import type { DatabaseDriver } from '@w/db'
import { receivedMutationBatchWrites } from './author-mutation-fanout'

type ProjectionDriver = Pick<DatabaseDriver, 'query' | 'exec' | 'transaction'>
// Acknowledged feed position; replay after reopening is idempotent. The engine keeps one key row,
// advancing its event_id once per apply, so acknowledgement needs no write/commit.
const consumed = new WeakMap<ProjectionDriver, number>()
const drains = new WeakMap<ProjectionDriver, Promise<boolean>>()

export function consumeAppliedPageKeys(driver: ProjectionDriver) {
  const previous = drains.get(driver) ?? Promise.resolve(false)
  const next = previous.then(() => drainAppliedPageKeys(driver))
  drains.set(driver, next)
  void next.finally(() => { if (drains.get(driver) === next) drains.delete(driver) }).catch(() => {})
  return next
}

/** Drain committed receive CDC into one local projection transaction; never writes personal cards. */
async function drainAppliedPageKeys(driver: ProjectionDriver) {
  const cursor = consumed.get(driver) ?? 0
  const pending = await driver.query<{eventId:number|null}>("SELECT change_cursor AS eventId FROM _turso_replication_state WHERE id=1 AND change_cursor>?", [cursor])
  const through = pending[0]?.eventId
  if (through === null || through === undefined) return false
  // Text-only CDC has no generic-card work. Check lifecycle work before opening a write
  // transaction; acknowledging this range is a cursor advance, not a queue-row delete.
  const work = await driver.query(`SELECT 1 FROM _turso_replication_applied_ops WHERE table_name='pages'
    UNION ALL SELECT 1 FROM _turso_replication_changed_keys c
      WHERE c.event_id>? AND c.table_name='retainedparts' AND c.key_name='pageid'
    UNION ALL SELECT 1 FROM _turso_replication_changed_keys c JOIN retainedParts r ON r.pageId=c.key_value
      JOIN flashcards f ON f.qid=r.qid WHERE c.event_id>? AND c.table_name='pages'
    UNION ALL SELECT 1 FROM _turso_replication_changed_keys c JOIN genericCards g ON g.pageId=c.key_value
      WHERE c.event_id>? AND c.table_name='pages' AND NOT EXISTS (SELECT 1 FROM pages p WHERE p.id=g.pageId)
      AND (g.deletedAt IS NOT NULL OR NOT EXISTS (SELECT 1 FROM flashcards f WHERE f.qid=g.qid))
    UNION ALL SELECT 1 FROM _turso_replication_changed_keys c JOIN genericCards g ON g.pageId=c.key_value
      JOIN pages p ON p.id=g.pageId WHERE c.event_id>? AND c.table_name='pages'
      AND (g.deletedAt IS NOT p.deletedAt OR g.deletedBy IS NOT p.deletedBy)
      AND (p.deletedAt IS NOT NULL OR EXISTS (SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',g.qid,p.rowid) a WHERE a.fields->>'qid'=g.qid))
    UNION ALL SELECT 1 FROM genericCards g WHERE g.deletedAt IS NOT NULL
      AND NOT EXISTS (SELECT 1 FROM flashcards f WHERE f.qid=g.qid) LIMIT 1`, [cursor,cursor,cursor,cursor])
  if (work.length === 0) { consumed.set(driver, through); return true }
  await driver.transaction(async tx => {
    // Receive projections are local bookkeeping, not a new authored transaction.
    await tx.exec("PRAGMA capture_data_changes_conn('off')")
    try {
      const batches = await tx.query<{pageId:string;updatedAt:number;editedBy:string;events:string}>(`SELECT p.id AS pageId,p.updatedAt,p.editedBy,f.value->'events' AS events
        FROM _turso_replication_applied_ops a CROSS JOIN json_each(a.frames) f JOIN pages p ON p.id=f.value->>'keyValue' WHERE a.table_name='pages'`)
      for (const statement of receivedMutationBatchWrites(batches)) {
        const query=statement.toSQL()
        await tx.exec(query.sql,query.params.map(parameter=>parameter.value))
      }
      await tx.exec("DELETE FROM _turso_replication_applied_ops WHERE table_name='pages'")
      // Removed parts carry the original removal's author/time and last source location.
      // Reconcile these immutable removal facts after all rows of an apply, including a regrant
      // whose page snapshot has no live question insertion to project.
      await tx.exec(`INSERT INTO genericCards(qid,pageId,node,fractionalIndex,importance,difficulty,promptType,ttaSeconds,cardKind,updatedAt,deletedAt,deletedBy)
        SELECT r.qid,r.pageId,r.node,COALESCE(r.content->>'fractionalIndex','a0'),
          p.importance,p.difficulty,p.promptType,p.ttaSeconds,p.cardKind,r.removedAt,r.removedAt,r.removedBy
        FROM retainedParts r JOIN flashcards p ON p.qid=r.qid JOIN pages source ON source.id=r.pageId
        WHERE r.pageId IN (SELECT key_value FROM _turso_replication_changed_keys WHERE event_id>? AND (table_name='pages' OR (table_name='retainedparts' AND key_name='pageid')))
          AND source.deletedAt IS NULL
          AND NOT EXISTS (SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',r.qid,source.rowid) a WHERE a.fields->>'qid'=r.qid)
          AND NOT EXISTS (SELECT 1 FROM retainedParts earlier WHERE earlier.pageId=r.pageId AND earlier.qid=r.qid
            AND (COALESCE(earlier.content->>'fractionalIndex','a0')<COALESCE(r.content->>'fractionalIndex','a0')
              OR (COALESCE(earlier.content->>'fractionalIndex','a0')=COALESCE(r.content->>'fractionalIndex','a0') AND earlier.node<r.node)))
        ON CONFLICT(qid) DO UPDATE SET deletedAt=excluded.deletedAt,deletedBy=excluded.deletedBy,
          node=excluded.node,fractionalIndex=excluded.fractionalIndex,updatedAt=excluded.updatedAt
        WHERE genericCards.pageId=excluded.pageId`, [cursor])
      await tx.exec(`UPDATE genericCards SET deletedAt=p.deletedAt,deletedBy=p.deletedBy
        FROM pages p WHERE genericCards.pageId=p.id
        AND p.id IN (SELECT key_value FROM _turso_replication_changed_keys WHERE event_id>? AND (table_name='pages' OR (table_name='retainedparts' AND key_name='pageid')))
        AND (p.deletedAt IS NOT NULL OR EXISTS (SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',genericCards.qid,p.rowid) a WHERE a.fields->>'qid'=genericCards.qid))
        AND (genericCards.deletedAt IS NOT p.deletedAt OR genericCards.deletedBy IS NOT p.deletedBy)`, [cursor])
      const changed = await tx.query<{key_value:string}>("SELECT key_value FROM _turso_replication_changed_keys WHERE event_id>? AND (table_name='pages' OR (table_name='retainedparts' AND key_name='pageid'))", [cursor])
      await projectPageAccessLoss(tx,changed.map(row => row.key_value))
      await tx.exec(`DELETE FROM genericCards WHERE deletedAt IS NOT NULL
        AND NOT EXISTS (SELECT 1 FROM flashcards p WHERE p.qid=genericCards.qid)`)

    } finally { await tx.exec("PRAGMA capture_data_changes_conn('full')") }
  })
  consumed.set(driver, through)
  return true
}

/** Loss of source access clears unobservable deletion evidence and removes unreferenced projections. */
export async function projectPageAccessLoss(driver: Pick<ProjectionDriver,'exec'>, pageIds: string[]) {
  const ids = JSON.stringify(pageIds)
  await driver.exec(`UPDATE genericCards SET deletedAt=NULL,deletedBy=NULL
    WHERE pageId IN (SELECT value FROM json_each(?))
    AND NOT EXISTS (SELECT 1 FROM pages p WHERE p.id=genericCards.pageId)`,[ids])
  await driver.exec(`DELETE FROM genericCards WHERE pageId IN (SELECT value FROM json_each(?))
    AND NOT EXISTS (SELECT 1 FROM pages p WHERE p.id=genericCards.pageId)
    AND NOT EXISTS (SELECT 1 FROM flashcards p WHERE p.qid=genericCards.qid)`,[ids])
}
